import React from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useUserProfile } from "../hooks/custom/useUserProfile";
import UserProfileHeader from "../components/ui/UserProfileHeader";
import UserProfileDetails from "../components/ui/UserProfileDetails";
import UserStatsCards from "../components/ui/UserStatsCards";
import { useNavigate } from "react-router-dom";
import Loader from "../components/ui/Loader";

// Import modals (these would need to be created)
import EditUserModal from "../components/modals/EditUserModal";
import DeleteUserModal from "../components/modals/DeleteUserModal";
import SuspendUserModal from "../components/modals/SuspendUserModal";

const UserProfilePage: React.FC = () => {
  usePageTitle("User Profile");
  const navigate = useNavigate();

  const {
    // Data
    user,
    trackingUser,
    loading,
    error,
    isAdmin,

    // Modal states
    showEditModal,
    showDeleteModal,
    showSuspendModal,
    isLoading,
    handleRefresh,

    // Handlers
    handleEditUser,
    handleDeleteUser,
    handleSuspendUser,
    handleConfirmDelete,
    handleConfirmSuspend,
    handleCloseEditModal,
    handleSaveEdit,
    handleCloseDeleteModal,
    handleCloseSuspendModal,

    // Feature flags
    marketingTogglePending,
    handleToggleMarketing,
  } = useUserProfile();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading User</h3>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-yellow-800 font-medium">User Not Found</h3>
          <p className="text-yellow-600 mt-1">
            The requested user could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Mock stats - in real app, these would come from API
  const userStats = {
    totalMessages: undefined,
    totalPosts: (user as any)?.totalPosts,
    totalComments: (user as any)?.totalComments,
    totalReplies: (user as any)?.totalReplies,
    totalLikes: (user as any)?.totalLikes,
    totalChats: (user as any)?.totalChats,
    totalLogins: (trackingUser as any)?.totalLogins,
    averageSessionDuration: (trackingUser as any)?.appUsageStats
      ?.averageSessionDuration,
  };

  return (
    <div className="p-6">
      {/* Header */}
      <UserProfileHeader
        user={user}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onSuspendUser={handleSuspendUser}
        isAdmin={isAdmin}
        onRefresh={handleRefresh}
        refreshing={isLoading}
        onToggleMarketing={handleToggleMarketing}
        marketingTogglePending={marketingTogglePending}
      />

      {/* Stats Cards */}
      <UserStatsCards
        user={user}
        stats={userStats}
        onOpenChats={() => navigate(`/messages?userId=${(user as any)?._id}`)}
      />

      {/* User Details */}
      <UserProfileDetails user={user} tracking={trackingUser} />

      {/* Modals */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        user={user}
        onSave={handleSaveEdit}
        isLoading={isLoading}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        user={user}
        onConfirmDelete={handleConfirmDelete}
        isLoading={isLoading}
      />

      <SuspendUserModal
        isOpen={showSuspendModal}
        onClose={handleCloseSuspendModal}
        user={user}
        onConfirmSuspend={handleConfirmSuspend}
        isLoading={isLoading}
      />
    </div>
  );
};

export default UserProfilePage;
